import styled, { css, keyframes } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";

const SLIDING_SIDEBAR_WIDTH = "380px";
const SIDEBAR_HEIGHT = "180px";

export type Direction = "top" | "right" | "bottom" | "left";

const slideInTop = keyframes`
  from { transform: translate3d(0, -105%, 0); }
  to { transform: translate3d(0, 0, 0); }
`;

const slideInRight = keyframes`
  from { transform: translate3d(105%, 0, 0); }
  to { transform: translate3d(0, 0, 0); }
`;

const slideInBottom = keyframes`
  from { transform: translate3d(0, 105%, 0); }
  to { transform: translate3d(0, 0, 0); }
`;

const slideInLeft = keyframes`
  from { transform: translate3d(-105%, 0, 0); }
  to { transform: translate3d(0, 0, 0); }
`;

const slideOutTop = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(0, -105%, 0); }
`;

const slideOutRight = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(105%, 0, 0); }
`;

const slideOutBottom = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(0, 105%, 0); }
`;

const slideOutLeft = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-105%, 0, 0); }
`;

export const SidebarContainer = styled.aside<{
    $isOpen: boolean;
    $animationType: string;
    $animationDirection: Direction;
}>`
    position: fixed;
    z-index: 1070;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    line-height: normal;
    background: ${colorsPalettes.carbon[0]};
    border: 1px solid #e6e6e6;
    visibility: hidden;
    will-change: transform;
    font-weight: 300;
    animation-duration: 0.4s;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;

    ${({ $animationDirection }) =>
        $animationDirection === "top" || $animationDirection === "bottom"
            ? css`
                  left: 0;
                  width: 100%;
                  height: ${SIDEBAR_HEIGHT};
                  max-height: 100%;
              `
            : css`
                  top: 0;
                  height: 100%;
                  width: ${SLIDING_SIDEBAR_WIDTH};
                  max-width: 100%;
              `}

    ${({ $animationDirection }) =>
        $animationDirection === "top" &&
        css`
            top: 0;
            transform: translate3d(0, -105%, 0);
        `}

    ${({ $animationDirection }) =>
        $animationDirection === "right" &&
        css`
            right: 0;
            transform: translate3d(105%, 0, 0);
        `}

    ${({ $animationDirection }) =>
        $animationDirection === "bottom" &&
        css`
            bottom: 0;
            transform: translate3d(0, 105%, 0);
        `}

    ${({ $animationDirection }) =>
        $animationDirection === "left" &&
        css`
            left: 0;
            transform: translate3d(-105%, 0, 0);
        `}

    ${({ $isOpen, $animationDirection }) =>
        $isOpen &&
        css`
            visibility: visible;
            animation-name: ${$animationDirection === "top"
                ? slideInTop
                : $animationDirection === "right"
                  ? slideInRight
                  : $animationDirection === "bottom"
                    ? slideInBottom
                    : slideInLeft};
        `}

    ${({ $isOpen, $animationDirection }) =>
        !$isOpen &&
        css`
            visibility: visible;
            animation-name: ${$animationDirection === "top"
                ? slideOutTop
                : $animationDirection === "right"
                  ? slideOutRight
                  : $animationDirection === "bottom"
                    ? slideOutBottom
                    : slideOutLeft};
        `}
`;

export const CloseButton = styled(IconButton)`
    position: absolute;
    z-index: 100;
    right: 25px;
    top: 21px;
    box-sizing: border-box;
    padding: 0;
    border: none;
    cursor: pointer;

    &:focus,
    &:hover {
        outline: none;
    }
`;
